import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Check, X } from 'lucide-react';
import type { UserDto, UserStatus } from '@bstm/shared';
import { api, ApiError } from '../api/client';
import { toast } from 'sonner';

interface AuditEvent {
  id: string;
  actorUserId: string;
  actorPhone: string | null;
  actorName: string | null;
  action: string;
  targetType: string;
  targetId: string;
  metadata: Record<string, unknown> | null;
  createdAt: number;
}

function fmtActor(e: AuditEvent): string {
  if (e.actorName) return e.actorName;
  if (e.actorPhone) return e.actorPhone;
  return e.actorUserId.slice(0, 8);
}

function NameCell({ user, onSaved }: { user: UserDto; onSaved: () => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(user.name ?? '');

  const save = useMutation({
    mutationFn: (name: string | null) =>
      api<{ user: UserDto }>(`/admin/users/${user.id}`, { method: 'PATCH', json: { name } }),
    onSuccess: () => {
      setEditing(false);
      onSaved();
      toast.success('Name updated');
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Failed'),
  });

  if (!editing) {
    return (
      <button
        onClick={() => {
          setDraft(user.name ?? '');
          setEditing(true);
        }}
        className="flex items-center gap-2 text-zinc-300 hover:text-white"
      >
        {user.name ?? <span className="text-zinc-600 italic">unset</span>}
        <Pencil size={12} className="text-zinc-600" />
      </button>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <input
        autoFocus
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save.mutate(draft.trim() || null);
          if (e.key === 'Escape') setEditing(false);
        }}
        className="text-sm py-1 px-2"
        placeholder="Display name"
      />
      <button
        onClick={() => save.mutate(draft.trim() || null)}
        className="text-emerald-400 hover:text-emerald-300"
      >
        <Check size={14} />
      </button>
      <button onClick={() => setEditing(false)} className="text-zinc-500 hover:text-zinc-300">
        <X size={14} />
      </button>
    </div>
  );
}

const TABS: Array<UserStatus | 'ALL'> = ['PENDING_APPROVAL', 'ACTIVE', 'SUSPENDED', 'REJECTED', 'ALL'];

export default function AdminUsers() {
  const [tab, setTab] = useState<UserStatus | 'ALL'>('PENDING_APPROVAL');
  const qc = useQueryClient();

  const usersQ = useQuery<{ users: UserDto[] }>({
    queryKey: ['admin', 'users', tab],
    queryFn: () => api(tab === 'ALL' ? '/admin/users' : `/admin/users?status=${tab}`),
  });

  const auditQ = useQuery<{ events: AuditEvent[] }>({
    queryKey: ['admin', 'audit'],
    queryFn: () => api('/admin/audit?limit=50'),
  });

  const useUserAction = (path: string) =>
    useMutation({
      mutationFn: (id: string) => api(`/admin/users/${id}/${path}`, { method: 'POST', json: {} }),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ['admin', 'users'] });
        qc.invalidateQueries({ queryKey: ['admin', 'audit'] });
        toast.success(`${path} ok`);
      },
      onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Failed'),
    });

  const approve = useUserAction('approve');
  const reject = useUserAction('reject');
  const suspend = useUserAction('suspend');
  const reinstate = useUserAction('reinstate');

  function refreshUsers() {
    qc.invalidateQueries({ queryKey: ['admin', 'users'] });
  }

  return (
    <div className="p-4 md:p-6">
      <h1 className="text-xl md:text-2xl font-semibold mb-4 md:mb-6">Admin · Users</h1>

      <div className="flex gap-1 mb-4 border-b border-zinc-800 overflow-x-auto -mx-4 md:mx-0 px-4 md:px-0">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-3 py-2 text-xs md:text-sm whitespace-nowrap border-b-2 -mb-px ${
              tab === t
                ? 'border-emerald-500 text-white'
                : 'border-transparent text-zinc-400 hover:text-white'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="card overflow-x-auto mb-6 md:mb-8">
        <table className="w-full text-sm">
          <thead className="bg-zinc-950 text-zinc-400 text-left">
            <tr>
              <th className="px-3 md:px-4 py-2">Phone</th>
              <th className="px-3 md:px-4 py-2">Name</th>
              <th className="hidden sm:table-cell px-4 py-2">Role</th>
              <th className="hidden md:table-cell px-4 py-2">Status</th>
              <th className="hidden lg:table-cell px-4 py-2">Created</th>
              <th className="px-3 md:px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {usersQ.data?.users.map((u) => (
              <tr key={u.id} className="border-t border-zinc-800">
                <td className="px-3 md:px-4 py-2 font-mono text-xs md:text-sm">{u.phone}</td>
                <td className="px-3 md:px-4 py-2">
                  <NameCell user={u} onSaved={refreshUsers} />
                </td>
                <td className="hidden sm:table-cell px-4 py-2">{u.role}</td>
                <td className="hidden md:table-cell px-4 py-2 text-xs text-zinc-400">{u.status}</td>
                <td className="hidden lg:table-cell px-4 py-2 text-zinc-500">{new Date(u.createdAt * 1000).toLocaleDateString()}</td>
                <td className="px-3 md:px-4 py-2">
                  <div className="flex gap-1 md:gap-2 justify-end flex-wrap">
                    {u.status === 'PENDING_APPROVAL' && (
                      <>
                        <button className="btn-primary text-xs px-2 py-1" onClick={() => approve.mutate(u.id)}>Approve</button>
                        <button className="btn-danger text-xs px-2 py-1" onClick={() => reject.mutate(u.id)}>Reject</button>
                      </>
                    )}
                    {u.status === 'ACTIVE' && u.role !== 'ADMIN' && (
                      <button className="btn-danger text-xs px-2 py-1" onClick={() => suspend.mutate(u.id)}>Suspend</button>
                    )}
                    {(u.status === 'SUSPENDED' || u.status === 'REJECTED') && (
                      <button className="btn-primary text-xs px-2 py-1" onClick={() => reinstate.mutate(u.id)}>Reinstate</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {usersQ.data?.users.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center text-zinc-500 py-8">No users in this view.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <h2 className="text-base md:text-lg font-semibold mb-3">Recent audit events</h2>
      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-950 text-zinc-400 text-left">
            <tr>
              <th className="px-3 md:px-4 py-2">When</th>
              <th className="px-3 md:px-4 py-2">Actor</th>
              <th className="px-3 md:px-4 py-2">Action</th>
              <th className="hidden sm:table-cell px-4 py-2">Target</th>
              <th className="hidden lg:table-cell px-4 py-2">Meta</th>
            </tr>
          </thead>
          <tbody>
            {auditQ.data?.events.map((e) => (
              <tr key={e.id} className="border-t border-zinc-800">
                <td className="px-3 md:px-4 py-2 text-zinc-500 text-xs whitespace-nowrap">{new Date(e.createdAt * 1000).toLocaleString()}</td>
                <td className="px-3 md:px-4 py-2 text-xs">{fmtActor(e)}</td>
                <td className="px-3 md:px-4 py-2 text-xs">{e.action}</td>
                <td className="hidden sm:table-cell px-4 py-2 font-mono text-xs">{e.targetType}/{e.targetId.slice(0, 8)}</td>
                <td className="hidden lg:table-cell px-4 py-2 font-mono text-xs text-zinc-500 truncate max-w-md">
                  {e.metadata ? JSON.stringify(e.metadata) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
